import {Moment} from 'moment-timezone'

import {AppName} from '../hproduce'

import {Iso8601, Uuid} from './atomic.types'
import {RunningTime, RunningTimeDto} from './runningTimes.types'
import {StoppageKind, shifthandoverStoppagesList} from './shifthandoverStoppages.types'
import {StoppageTypeWithCode, StoppageTypeWithReason, StoppageType} from './stoppage.types'
import {TaskRepetitionDto} from './taskRepetition.types'
import {FilterTimeRange} from './timeAndDate.types'

// Find a list of priorities in the usePriorities hook
export type Priority = number

export type PriorityItem = {
  priorityNumber: string
  priorityNumberText?: string | null
  language?: string | null
}

export type PrioritiesTranslationMap = Map<Priority, string>

// RFC 7807 based Error payload
export type HttpProblem = {
  type: string
  title?: string
  detail?: string
  instance?: string
}

export type HttpError = HttpProblem & {code: number}

export type ShifthandoverStoppageCode = {
  code: string
  description: string
  type: StoppageKind
  stoppageCategory?: ShiftEventCategory
}

export type DowntimeStoppageCode = {
  code: string
  description: string
  type: StoppageKind
  priority?: number
}

export const eventTypesInterchangeable = [
  'malfunction',
  'healthAndSafety',
  'task',
  'information',
  'idea',
  'environment',
  'parameterChange',
  'systemChangeRequest',
  'quality'
] as const
export const eventTypes = [
  ...shifthandoverStoppagesList,
  ...eventTypesInterchangeable,
  'spot'
] as const
export type EventType = (typeof eventTypes)[number]

export const equipmentIdTypes = [
  'technicalId',
  'sapNumber',
  'processId',
  'mainEquipmentCustomId',
  'janusId'
] as const
export type EquipmentIdType = (typeof equipmentIdTypes)[number]

export const EquipmentCategory = {
  CEMENT: 'cement',
  CLINKER: 'clinker',
  CRUSHER: 'crusher',
  RAW: 'raw',
  COAL: 'coal',
  UNKNOWN: 'unknown',
  ENVIRONMENTAL: 'Environmental',
  PACKING: 'Packing',
  FUEL: 'Fuel'
} as const
export type EquipmentCategory = (typeof EquipmentCategory)[keyof typeof EquipmentCategory]

export enum ShiftEventCategory {
  Mechanical = 'mechanical',
  Electrical = 'electrical',
  Quality = 'quality',
  Process = 'process',
  Environment = 'environment',
  HealthAndSafety = 'healthAndSafety',
  Production = 'production',
  Cleaning = 'cleaning',
  Cost = 'cost',
  Other = 'other'
}

export const maintenanceNotifications = ['withNotifications', 'withoutNotifications'] as const
export type MaintenanceNotification = (typeof maintenanceNotifications)[number]

// Note: cementMill, rawMill, coalMill and crusher are currently not equipment types
// they are only possible equipment categories which map to the stoppage types
export const EquipmentType = {
  KILN: 'kiln',
  VERTICAL_MILL: 'verticalMill',
  ROLLER_PRESS: 'rollerPress',
  DOSING_EQUIPMENT: 'dosingEquipment',
  BALL_MILL: 'ballMill',
  OTHER: 'other'
} as const
export type EquipmentType = (typeof EquipmentType)[keyof typeof EquipmentType]

export type EquipmentData = {
  idType?: EquipmentIdType
  id: string
  text?: string
  equipmentCategory?: EquipmentCategory
  equipmentType?: EquipmentType
}

export type RunningTimesEquipmentData = {
  idType?: EquipmentIdType
  id: string
  text?: string
  matchingId: string
  equipmentCategory?: EquipmentCategory
  equipmentType?: EquipmentType
}

export type EquipmentRunningTimesDto = {
  equipment: RunningTimesEquipmentData
  runningTimes: RunningTimeDto[]
  totalRunning: string
  totalStopped: string
}
export type EquipmentRunningTimes = {
  equipment: RunningTimesEquipmentData
  runningTimes: RunningTime[]
  totalRunning: string
  totalStopped: string
}
export type PlantRunningTimesDto = {
  plantId: string
  equipmentsWithRunningTimes: EquipmentRunningTimesDto[]
}

export type StoppageDetails =
  | {
      stoppageType: StoppageTypeWithCode
      stoppageCode: string
    }
  | {
      stoppageType: StoppageTypeWithReason
      stoppageReason: string
    }

export type ProcessStage = {
  code: string
  name: string
}

export type EquipmentPlain = {
  equipment: EquipmentData
  processStage?: {
    processStep: string
    processStepValue: string
  }
  path?: EquipmentData[]
  mainEquipment?: EquipmentData
  defaultWorkCenterId?: string
  defaultPlannerGroup?: string
}

export type WorkCenter = {id: string; text: string}

export enum WorkOrderTarget {
  Sap = 'sap',
  Infor = 'infor'
}

export type InforWorkOrder = {
  requestId: string
}

export type User = {
  id: Uuid
  name: string
}

export interface AttachmentFile<TId extends string | number = number> {
  id?: TId
  preview: string
  name: string
  contentType: string
  isImage?: boolean
  originalFile?: File
}

export interface Info {
  // username
  createdBy: User
  modifiedBy: string
  created: Iso8601
  lastModified: Iso8601
  lastModifiedStatus?: Iso8601
}

export interface Id {
  id: string
}

export type Assignees =
  | {
      assignees: User[]
    }
  | {
      assignees: never
    }

export const statusListWithCancellationOption = ['pending', 'ongoing', 'done', 'cancelled'] as const
export type StatusWithCancellation = (typeof statusListWithCancellationOption)[number]

export type TaskPreviewData = TaskRepetitionDto & {
  itemsCount: number
  startDate?: Iso8601
}

// Note: Not present in shifthandover events
// TODO: check with BE on implementation
export type EventAppInfo = {app: AppName; plantId: string; externalId}

export type SharedEventStructureDto = {
  category: ShiftEventCategory
  description: string
  title: string
  commentCount: number

  // optional
  isHighlighted?: boolean
  equipment?: EquipmentData
  mainEquipment?: EquipmentData
  processStage?: string
  priority?: Priority
  schedule?: {
    startDate: Iso8601
    endDate: Iso8601
  }
  inforWorkOrder?: InforWorkOrder
}

export type ShiftEventTaskDto = Omit<SharedEventStructureDto, 'schedule'> &
  Assignees & {
    dueDate: Iso8601
    eventType: 'task'
    status: StatusWithCancellation

    // optional
    toggleAssigneeNotification?: boolean
    repetitionInfo?: TaskRepetitionDto | null
    doNotStartBefore?: Iso8601
    isChecklistsCreationFailed?: boolean
  }

export type StatusWithNoneAndCancellationAndApproverAndWorkOrderStates =
  | 'none'
  | 'pending'
  | 'ongoing'
  | 'done'
  | 'cancelled'
  | 'approved'
  | 'rejected'
  | 'waitingForApproval'
  | 'workOrderCreated'
  | 'implemented'
  | 'implementedTemporary'
  | 'reverted'

export enum EventsTab {
  Events = 'Events',
  WorkOrders = 'WorkOrders',
  Notifications = 'Notifications'
}

export type ShiftEventCommentDto = {
  id: string
  description: string
  createDate: Iso8601
  isEditable?: boolean
  creator: string
  creatorId: Uuid
}

export type ShiftEventComment = {
  id: string
  description: string
  createDate: Moment
  isEditable?: boolean
  creator: string
  creatorId: Uuid
}

export enum ShiftEventsSortBy {
  Created = 'created',
  Details = 'details',
  EventType = 'eventType',
  Title = 'title',
  EquipmentNo = 'equipmentNo',
  MainEquipmentNo = 'mainEquipmentNo',
  Category = 'category',
  ProcessStage = 'processStage',
  MaintenanceNotificationId = 'maintenanceNotificationId',
  Status = 'status',
  LastModified = 'lastModified'
}

export enum SortOrder {
  Ascending = 'ascending',
  Descending = 'descending'
}

/* search / Filter */

export type StoppageCodesFilter = {
  type: StoppageType
  codes: string[]
}

export type EventSearchCriteria = {
  eventType?: EventType[]
  category?: ShiftEventCategory[]
  processStage?: string[]
  status?: StatusWithNoneAndCancellationAndApproverAndWorkOrderStates[]
  priority?: Priority[]
  stoppageCodesFilter?: StoppageCodesFilter[]

  timeRange: FilterTimeRange
  sortBy?: ShiftEventsSortBy
  sortOrder?: SortOrder
  itemsPerPage?: number
  pageNumber?: number
  equipment?: EquipmentData
  mainEquipment?: EquipmentData
  upmDepartmentIds?: string[]
  changeFunctions?: string[]
  freeText?: string
  maintenanceNotificationExists?: boolean
  pxTrendTags?: string[]
}

enum ChangeFunctionCode {
  UserAdministration = 'UserAdministration',
  AlarmLimitModification = 'AlarmLimitModification',
  SwitchingLimitModification = 'SwitchingLimitModification',
  ProcessFeedbackModification = 'ProcessFeedbackModification',
  AnalogMeasurementSimulation = 'AnalogMeasurementSimulation',
  StartInterlockModification = 'StartInterlockModification',
  OperationInterlockModification = 'OperationInterlockModification',
  ProtectionInterlockModification = 'ProtectionInterlockModification',
  ManualModeNonInterlockOperation = 'ManualModeNonInterlockOperation',
  StopSequenceModification = 'StopSequenceModification',
  OtherOrGeneral = 'OtherOrGeneral'
}

export type ChangeFunction = {
  id: string
  code: ChangeFunctionCode
  name: string
}
